import { FormEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Loader2, Lock, Mail } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/app/store/auth.store';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { useSignupEasterEgg } from '@/features/auth/signup/hooks/use-signup-easter-egg';
import { useLoginMutation } from '@/features/auth/login/hooks/use-login-mutations';
import { RequiredTermsLinks } from '@/features/auth/public-terms/components/RequiredTermsLinks';
import { LoginError } from '@/api/auth';
import { isValidPassword } from '@/shared/utils/password';
import { ShcLogo } from '@/components/brand/ShcLogo';
import { AuthFooterOrganization } from '@/features/auth/institution/AuthFooterOrganization';

// Email format: local@domain.TLD where the TLD is >=2 ASCII letters (aligns the FE to the spec email rule).
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

interface FieldErrors {
  email: string;
  password: string;
}

interface PageStatus {
  message: string;
  showResetLink: boolean;
}

interface LocationState {
  from?: { pathname: string };
}

export const AdminLoginPage = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const { onLogoClick } = useSignupEasterEgg();
  const loginMutation = useLoginMutation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({ email: '', password: '' });
  const [pageStatus, setPageStatus] = useState<PageStatus | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [showFindIdModal, setShowFindIdModal] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  // SHCT-689 — PRD 21 §7.2 `ACCOUNT_WITHDRAWN`, kept apart from the 비활성 modal above.
  const [showWithdrawnModal, setShowWithdrawnModal] = useState(false);
  const [showDuplicateSessionModal, setShowDuplicateSessionModal] = useState(false);

  const isLoading = loginMutation.isPending;
  const inputsDisabled = isLocked || isLoading;

  const validate = (): boolean => {
    const next: FieldErrors = { email: '', password: '' };
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      next.email = t('login.fields.emailRequired', { ns: 'auth' });
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      next.email = t('login.fields.emailInvalid', { ns: 'auth' });
    } else if (trimmedEmail.length < 5 || trimmedEmail.length > 50) {
      next.email = t('login.fields.emailLength', { ns: 'auth' });
    }

    if (!password) {
      next.password = t('login.fields.passwordRequired', { ns: 'auth' });
    } else if (!isValidPassword(password)) {
      // PRD 21 §5.2 — 8~64 chars with letter + number + symbol.
      next.password = t('login.fields.passwordInvalid', { ns: 'auth' });
    }

    setFieldErrors(next);
    return !next.email && !next.password;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (inputsDisabled) return;
    setPageStatus(null);

    if (!validate()) return;

    loginMutation.mutate(
      { email: email.trim(), password },
      {
        onSuccess: (result) => {
          login({
            email: result.me.email,
            name: result.me.name,
            roles: result.me.roles,
            isRoot: result.me.isRoot,
            currentAdminId: result.me.id,
            status: result.me.status,
            permissions: result.me.permissions,
            mustChangePassword: result.mustChangePassword,
          });

          if (result.mustChangePassword) {
            navigate('/reset-password?mode=forced', { replace: true });
            return;
          }

          const from = (location.state as LocationState | null)?.from?.pathname;
          navigate(from && from !== '/login' ? from : '/home', { replace: true });
        },
        onError: (error) => handleLoginError(error),
      },
    );
  };

  const handleLoginError = (error: unknown) => {
    if (!(error instanceof LoginError)) {
      // Network / server unreachable.
      toast.error(t('login.errors.serverError', { ns: 'auth' }));
      return;
    }
    switch (error.code) {
      case 'LOGIN_BAD_CREDENTIALS': {
        const attempts = error.meta?.failCount as number | undefined;
        const max = (error.meta?.maxFail as number | undefined) ?? 5;
        const message =
          attempts !== undefined
            ? t('login.errors.invalidCredentials', { ns: 'auth', attempts, max })
            : t('login.errors.invalidCredentialsNoCounter', { ns: 'auth' });
        setPageStatus({ message, showResetLink: false });
        return;
      }
      case 'ACCOUNT_LOCKED':
        setIsLocked(true);
        setPageStatus({
          message: t('login.errors.accountLocked', { ns: 'auth' }),
          showResetLink: true,
        });
        return;
      case 'ACCOUNT_INACTIVE':
        setShowInactiveModal(true);
        return;
      // SHCT-689 — PRD 21 v2.21 §7.2 splits the account-state codes. 비활성 and 탈퇴 used to share
      // `ACCOUNT_INACTIVE`, so a withdrawn admin was told to contact an administrator about an
      // account nobody can restore. They are opposites on recoverability, so they get their own
      // code and their own copy; the 비활성 wording is unchanged.
      case 'ACCOUNT_WITHDRAWN':
        setShowWithdrawnModal(true);
        return;
      case 'LOGIN_RATE_LIMITED':
        toast.error(t('login.errors.serverError', { ns: 'auth' }));
        return;
      // PRD 00 §8.2 separates "서버 응답 없음" from "데이터베이스 오류": a connection failure is
      // retryable by the user, a 5xx needs an admin. `login()` synthesizes the NETWORK sentinel when
      // the request never reached the server, so it arrives here as a LoginError — without this case
      // it fell through to `default` and showed the 5xx copy instead (SHCT-623).
      case 'NETWORK':
        toast.error(t('login.errors.serverError', { ns: 'auth' }));
        return;
      case 'DUPLICATE_SESSION':
        // Defensive: the real backend evicts the prior session silently, but keep explicit handling.
        // §7.1 Business Logic (state conflict) → Modal; eviction + redirect happen on acknowledge.
        setShowDuplicateSessionModal(true);
        return;
      default:
        toast.error(t('login.errors.systemError', { ns: 'auth' }));
        return;
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: '' }));
    // Lockout is per-account — switching email clears the locked UI.
    if (isLocked) {
      setIsLocked(false);
      setPageStatus(null);
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: '' }));
  };

  return (
    <main className="login-page">
      <div className="login-page__container">
        <div className="login-page__logo" onClick={onLogoClick} style={{ cursor: 'default' }}>
          <div className="login-page__logo-icon">
            <ShcLogo />
          </div>
          <h1 className="login-page__logo-text">{t('brand.serviceName', { ns: 'labels' })}</h1>
        </div>

        <p className="login-page__subtitle">{t('login.subtitle', { ns: 'auth' })}</p>

        <form className="login-page__form" onSubmit={handleSubmit} noValidate>
          <div className="login-page__field">
            <label className="login-page__label" htmlFor="loginEmail">
              {t('login.email', { ns: 'auth' })}
            </label>
            <div className="login-page__input-wrap">
              <Mail className="login-page__input-icon" size={24} />
              <input
                id="loginEmail"
                className={`login-page__input ${fieldErrors.email ? 'login-page__input--error' : ''}`}
                type="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder={t('login.emailPlaceholder', { ns: 'auth' })}
                autoComplete="email"
                disabled={inputsDisabled}
              />
            </div>
            {fieldErrors.email && (
              <p className="login-page__error">{fieldErrors.email}</p>
            )}
          </div>

          <div className="login-page__field">
            <label className="login-page__label" htmlFor="loginPassword">
              {t('login.password', { ns: 'auth' })}
            </label>
            <div className="login-page__input-wrap">
              <Lock className="login-page__input-icon" size={24} />
              <input
                id="loginPassword"
                className={`login-page__input ${fieldErrors.password ? 'login-page__input--error' : ''}`}
                type="password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder={t('login.passwordPlaceholder', { ns: 'auth' })}
                autoComplete="current-password"
                maxLength={64}
                disabled={inputsDisabled}
              />
            </div>
            {fieldErrors.password && (
              <p className="login-page__error">{fieldErrors.password}</p>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-lg btn-primary login-page__submit"
            disabled={inputsDisabled}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="spin" />
                {t('login.submitting', { ns: 'auth' })}
              </>
            ) : (
              t('login.submit', { ns: 'auth' })
            )}
          </button>

          {pageStatus && (
            <p
              className={`login-page__auth-status ${
                isLocked
                  ? 'login-page__auth-status--warning'
                  : 'login-page__auth-status--error'
              }`}
              aria-live="polite"
            >
              {pageStatus.message}
              {pageStatus.showResetLink && (
                <Link
                  to="/reset-password"
                  className="login-page__auth-reset"
                  style={{ marginLeft: 6 }}
                >
                  {t('login.goToResetPassword', { ns: 'auth' })}
                </Link>
              )}
            </p>
          )}
        </form>

        <div className="login-page__links">
          <button
            type="button"
            className="login-page__link"
            onClick={() => setShowFindIdModal(true)}
          >
            {t('login.findId', { ns: 'auth' })}
          </button>
          <span className="login-page__links-sep">|</span>
          <Link to="/reset-password" className="login-page__link">
            {t('login.forgotPassword', { ns: 'auth' })}
          </Link>
        </div>
      </div>

      <footer className="login-page__footer">
        {/* Organization details come from GET /admin/institution (V129), not from the label catalog. */}
        <AuthFooterOrganization />

        {/* Dynamic — links only PUBLISHED terms marked `required` (FEATURE_041). */}
        <RequiredTermsLinks
          className="login-page__footer-terms"
          sepClassName="login-page__footer-sep"
        />
      </footer>

      <InfoModal
        open={showFindIdModal}
        title={t('login.findIdModal.title', { ns: 'auth' })}
        body={
          <>
            <p>
              <Trans
                i18nKey="login.findIdModal.body"
                ns="auth"
                components={{ b: <strong /> }}
              />
            </p>
            <ul
              style={{
                margin: '8px 0 0',
                paddingLeft: 18,
                lineHeight: 1.7,
                listStyleType: 'disc',
              }}
            >
              <li>{t('login.findIdModal.bullet1', { ns: 'auth' })}</li>
              <li>{t('login.findIdModal.bullet2', { ns: 'auth' })}</li>
            </ul>
          </>
        }
        confirmLabel={t('login.findIdModal.confirm', { ns: 'auth' })}
        confirmFullWidth
        onClose={() => setShowFindIdModal(false)}
      />

      <InfoModal
        open={showInactiveModal}
        title={t('login.inactiveModal.title', { ns: 'auth' })}
        body={t('login.inactiveModal.body', { ns: 'auth' })}
        confirmLabel={t('login.inactiveModal.confirm', { ns: 'auth' })}
        onClose={() => setShowInactiveModal(false)}
      />

      <InfoModal
        open={showWithdrawnModal}
        title={t('login.withdrawnModal.title', { ns: 'auth' })}
        body={t('login.withdrawnModal.body', { ns: 'auth' })}
        confirmLabel={t('login.withdrawnModal.confirm', { ns: 'auth' })}
        onClose={() => setShowWithdrawnModal(false)}
      />

      <InfoModal
        open={showDuplicateSessionModal}
        title={t('login.duplicateSessionModal.title', { ns: 'auth' })}
        body={t('login.errors.duplicateSession', { ns: 'auth' })}
        confirmLabel={t('login.duplicateSessionModal.confirm', { ns: 'auth' })}
        onClose={() => {
          setShowDuplicateSessionModal(false);
          logout();
          navigate('/login', { replace: true });
        }}
      />
    </main>
  );
};

export default AdminLoginPage;
